import { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, Box, IconButton, MenuItem, Menu, Typography, Avatar
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';

export const Header = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);
  const [navMenuAnchor, setNavMenuAnchor] = useState(null);

  const handleAvatarMenu = (event) => setAvatarMenuAnchor(event.currentTarget);
  const closeAvatarMenu = () => setAvatarMenuAnchor(null);

  const handleNavMenu = (event) => {
    setNavMenuAnchor((prev) => (prev ? null : event.currentTarget));
  };

  const closeNavMenu = () => setNavMenuAnchor(null);

  const toolbarRef = useRef(null);

  useEffect(() => {
    const setCssVar = () => {
      if (toolbarRef.current) {
        const h = Math.round(toolbarRef.current.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--app-header-height', `${h}px`);
      }
    };

    setCssVar();
    window.addEventListener('resize', setCssVar);
    return () => window.removeEventListener('resize', setCssVar);
  }, []);

  const handleLogout = () => {
    auth.logout()
    closeAvatarMenu();
    navigate('/');
  };

  const profileOpen = () => {
    closeAvatarMenu();
    navigate('/profile');
  };

  const restoreOpen = () => {
    closeAvatarMenu();
    navigate('superadmin/restoredata');
  };

  const navigateTo = (path) => {
    closeNavMenu();
    navigate(path);
  };

  const getInitials = (name) => { //Initials from users first name and last name, for the avatar
    if (!name) return "?";
    const parts = name.trim().split(" ");
    return parts.map(p => p[0]).slice(0, 2).join("").toUpperCase();
  };

  // Define what each role is allowed to see
  const menuItems = [
    { label: 'Plans', path: '/plans', roles: ['superadmin', 'admin', 'useredit', 'user'] },
    { label: 'Builders', path: '/builders', roles: ['superadmin', 'admin', 'useredit'] },
    { label: 'Team', path: '/team', roles: ['superadmin', 'admin', 'useredit'] },
    { label: 'Settings', path: '/settings', roles: ['superadmin', 'admin', 'useredit'] }
  ];

  const allowedMenuItems = menuItems.filter(item =>
    item.roles.includes(auth.userRole)
  );

  return (
    <AppBar position="sticky" sx={{ background: 'white', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' }}>
      <Toolbar ref={toolbarRef}>

        {/* Hamburger + Logo */}
        <IconButton edge="start" sx={{ color: '#0000008F', ml: '-12px' }} onClick={handleNavMenu}>
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1, fontSize: '1.5rem', fontWeight: 600 }}>
          <img
            src="/horizontal.svg"
            alt="Planmark by OcuBuild"
            style={{ width: '177px', display: 'block', margin: '0 auto' }}
          />
        </Typography>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '80px', height: '40px', marginRight: '24px' }}>
          <IconButton onClick={handleAvatarMenu}>
            <Avatar sx={{ bgcolor: '#BDBDBD' }}>
              {getInitials(auth.userName)}
            </Avatar>
          </IconButton>
          <IconButton
            onClick={handleAvatarMenu}
            size="small"
            sx={{
              ml: 0.5,
              color: '#0000008F',
              '&:hover': { color: '#1976d2' }
            }}
          >
            <ArrowDropDownIcon />
          </IconButton>
        </Box>

        {/* Avatar menu */}
        <Menu
          anchorEl={avatarMenuAnchor}
          open={Boolean(avatarMenuAnchor)}
          onClose={closeAvatarMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              width: 250,  // wider profile menu
              mt: 1.5       // small space to avoid overlapping avatar
            }
          }}
        >
          {auth.userRole === "superadmin" && localStorage.getItem("orgId") === "84a4c617-0681-4a75-b6d7-bcaeca5a95a9" && (
           <MenuItem onClick={restoreOpen} sx={{ color: '#E65100', fontWeight: 500 }}><ManageAccountsRoundedIcon sx={{ mr: 1, color: '#E65100' }} />
              Super Admin Tools
           </MenuItem>
          )}
          <MenuItem onClick={profileOpen}>Profile</MenuItem>
          <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
        </Menu>

        {/* Nav menu */}
        <Menu

          anchorEl={navMenuAnchor}
          open={Boolean(navMenuAnchor)}
          onClose={closeNavMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { width: '200px' } }}
        >
          {allowedMenuItems.map(item => (
            <MenuItem key={item.label} onClick={() => navigateTo(item.path)}>
              {item.label}
            </MenuItem>
          ))}
        </Menu>

      </Toolbar>
    </AppBar>
  );
};
